import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Row, Col, Form, Button, Table, Alert, Spin } from 'antd';
import { getQueryTypes, setupDefaultPrices, getTenantById, updateTenantPrice } from '../../services/api';
import { authService } from '../../services/auth';

interface Price {
  id: string;
  queryTypeName: string;
  price: number;
  queryTypeId: string;
}

const PriceConfigPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prices, setPrices] = useState<Price[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
  }, [tenantId]);

  const loadData = async () => {
    if (!tenantId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Carregar tipos de consulta
      const queryTypesResponse = await getQueryTypes();
      
      if (!queryTypesResponse.success) {
        throw new Error('Falha ao carregar tipos de consulta');
      }
      
      // Carregar tenant e seus preços
      const tenantResponse = await getTenantById(tenantId);
      
      if (!tenantResponse.success) {
        throw new Error('Falha ao carregar dados do cliente');
      }
      
      const tenant = tenantResponse.tenant;
      const allQueryTypes = queryTypesResponse.data;
      
      // Combinar os tipos de consulta com os preços específicos do tenant
      const pricesData: Price[] = allQueryTypes.map(queryType => {
        const tenantPrice = tenant.prices.find(price => price.queryTypeId === queryType.id);
        
        return {
          id: tenantPrice?.id || '',
          queryTypeName: queryType.name,
          price: tenantPrice?.price || queryType.defaultPrice,
          queryTypeId: queryType.id
        };
      });
      
      setPrices(pricesData);
      
      // Configurar valores iniciais do formulário
      const initialValues = {};
      pricesData.forEach(price => {
        initialValues[price.queryTypeId] = price.price;
      });
      
      form.setFieldsValue(initialValues);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    if (!tenantId) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Atualizar preços individualmente
      const updatePromises = Object.keys(values).map(queryTypeId => {
        const price = values[queryTypeId];
        return updateTenantPrice(tenantId, queryTypeId, price);
      });
      
      await Promise.all(updatePromises);
      
      setSuccess('Preços atualizados com sucesso');
      loadData(); // Recarregar dados para mostrar os novos preços
    } catch (err) {
      console.error('Erro ao salvar preços:', err);
      setError('Erro ao salvar preços. Por favor, tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleSetupDefaultPrices = async () => {
    if (!tenantId) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await setupDefaultPrices(tenantId);
      
      if (!response.success) {
        throw new Error(response.error || 'Falha ao configurar preços padrão');
      }
      
      setSuccess('Preços padrão configurados com sucesso');
      loadData(); // Recarregar dados
    } catch (err) {
      console.error('Erro ao configurar preços padrão:', err);
      setError('Erro ao configurar preços padrão. Por favor, tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Tipo de Consulta',
      dataIndex: 'queryTypeName',
      key: 'queryTypeName',
    },
    {
      title: 'Preço (créditos)',
      dataIndex: 'queryTypeId',
      key: 'price',
      render: (queryTypeId: string) => (
        <Form.Item
          name={queryTypeId}
          rules={[
            { required: true, message: 'Informe o preço' },
            { type: 'number', min: 0, message: 'O preço deve ser maior ou igual a zero' }
          ]}
          noStyle
        >
          <Form.InputNumber 
            min={0} 
            step={0.5} 
            precision={2}
            style={{ width: '100%' }}
          />
        </Form.Item>
      ),
    },
  ];

  return (
    <Card title="Configuração de Preços" style={{ marginBottom: 20 }}>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      {success && <Alert message={success} type="success" showIcon style={{ marginBottom: 16 }} />}
      
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <p>
            Configure os preços (em créditos) que serão cobrados de cada cliente para cada tipo de consulta.
          </p>
          
          <Table
            dataSource={prices}
            columns={columns}
            rowKey="queryTypeId"
            pagination={false}
            size="middle"
          />
          
          <Row justify="space-between" style={{ marginTop: 24 }}>
            <Col>
              <Button 
                type="default" 
                onClick={handleSetupDefaultPrices} 
                loading={saving}
              >
                Configurar Preços Padrão
              </Button>
            </Col>
            <Col>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={saving}
              >
                Salvar Alterações
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Card>
  );
};

export default PriceConfigPage; 